import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { saveAs } from 'file-saver';

import server from '../../../api/server';
import SearchInput from '../ui/SearchInput';
import BankStatementThumb from './BankStatementThumb';
import FilePreviewModal from './FilePreviewModal';
import { getUploadStamp, getUploadMillis } from './fileHelpers';
import { DownloadIcon, ChevronDownIcon, GridIcon, ListIcon, EyeIcon } from '../ui/icons';

// Redesign of the illion bank statement view to match the document collection
// view: real first-page PDF previews, grid/list toggle, search, sort and a
// lightbox. Same /bankstatements/data fetch on mount and /bankstatements/pdf
// blob download as before.
const BankStatementsApplicant = ({ applicantId }) => {
    const bankstatements = useSelector((s) => s.admin.active_application.bankstatements);
    const activeApplication = useSelector((s) => s.admin.active_application);
    const user = useSelector((s) => s.user);

    // The original fetched the parsed statement HTML on mount; the data is
    // not rendered by the current UI but the fetch is kept for parity.
    const [, setData] = useState(null);

    const viewPrefKey = `admin2:bankView:${user.email || 'default'}`;
    const sortPrefKey = `admin2:bankSort:${user.email || 'default'}`;
    const readPref = (key, allowed, fallback) => {
        try {
            const saved = window.localStorage.getItem(key);
            return allowed.includes(saved) ? saved : fallback;
        } catch (e) {
            return fallback;
        }
    };

    const [query, setQuery] = useState('');
    const [viewMode, setViewMode] = useState(() => readPref(viewPrefKey, ['grid', 'list'], 'grid'));
    const [sort, setSort] = useState(() => readPref(sortPrefKey, ['recent', 'oldest', 'name'], 'recent'));
    const [collapsed, setCollapsed] = useState({});
    const [previewIndex, setPreviewIndex] = useState(null);

    // uri -> { blob, url }; shared by thumbnails, the lightbox and downloads.
    const cache = useRef(new Map());

    useEffect(() => {
        const fetchData = async () => {
            if (bankstatements == null) return;
            const response = await server.post('/bankstatements/data', {
                id: user.token,
                application_id: bankstatements.application_id,
                applicant_id: applicantId,
            });
            if (response.data && response.data.success == true) {
                setData(response.data.htmlArray);
            }
        };
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        try {
            window.localStorage.setItem(viewPrefKey, viewMode);
        } catch (e) {
            /* localStorage unavailable */
        }
    }, [viewMode, viewPrefKey]);

    useEffect(() => {
        try {
            window.localStorage.setItem(sortPrefKey, sort);
        } catch (e) {
            /* localStorage unavailable */
        }
    }, [sort, sortPrefKey]);

    useEffect(
        () => () => {
            cache.current.forEach((entry) => URL.revokeObjectURL(entry.url));
            cache.current.clear();
        },
        []
    );

    const applicant = activeApplication.application.applicants[applicantId];
    const statements = bankstatements.applicants[applicantId]
        ? bankstatements.applicants[applicantId].statements
        : undefined;

    const fetchBlob = async (uri) => {
        const response = await server.post(
            '/bankstatements/pdf',
            {
                uri,
                applicant_id: applicantId,
                application_id: activeApplication.application.id,
            },
            { responseType: 'blob' }
        );
        if (!response.data) return null;
        return new Blob([response.data], { type: 'application/pdf' });
    };

    const loadPdf = async (uri) => {
        if (cache.current.has(uri)) return cache.current.get(uri);
        const blob = await fetchBlob(uri);
        if (!blob) return null;
        const entry = { blob, url: URL.createObjectURL(blob) };
        cache.current.set(uri, entry);
        return entry;
    };

    // FilePreviewModal calls loadPreview(file); adapt it to the uri-based fetch.
    const loadPreview = (file) => loadPdf(file.uri);

    const downloadFile = async (file) => {
        const cached = cache.current.get(file.uri);
        const blob = cached ? cached.blob : await fetchBlob(file.uri);
        if (blob) saveAs(blob, file.file_name);
    };

    // Build the statement groups, apply search + sort, and flatten the visible
    // PDF files (in render order) for the lightbox to page through.
    const { groups, flatFiles } = useMemo(() => {
        const result = { groups: [], flatFiles: [] };
        if (!statements) return result;

        const q = query.trim().toLowerCase();

        let built = Object.keys(statements).map((key) => {
            const group = statements[key];
            const pdfs = (group.statements || [])
                .filter((s) => s.type === '.pdf')
                .map((s) => {
                    const fileName = s.URI.split('/').pop();
                    return {
                        uri: s.URI,
                        path: s.URI, // unique key for flat-list indexing
                        file_name: fileName,
                        original_name: fileName,
                        uploaded_at: group.uploaded_at,
                        uploaded_date: group.date,
                    };
                });
            return { key, name: group.name || 'Bank statement', group, files: pdfs };
        });

        if (q) {
            built = built
                .map((g) => {
                    const nameMatch = g.name.toLowerCase().includes(q);
                    const files = nameMatch
                        ? g.files
                        : g.files.filter((f) => f.file_name.toLowerCase().includes(q));
                    return { ...g, files };
                })
                .filter((g) => g.files.length > 0);
        }

        built.sort((a, b) => {
            if (sort === 'name') return a.name.localeCompare(b.name);
            const diff = getUploadMillis(b.group) - getUploadMillis(a.group);
            return sort === 'oldest' ? -diff : diff;
        });

        result.groups = built;
        built.forEach((g) => g.files.forEach((f) => result.flatFiles.push(f)));
        return result;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [statements, query, sort]);

    const flatIndexOf = (file) => flatFiles.findIndex((f) => f.uri === file.uri);
    const toggle = (key) => setCollapsed((prev) => ({ ...prev, [key]: !prev[key] }));

    if (applicant == undefined) return null;

    const renderFile = (file) => {
        const idx = flatIndexOf(file);
        const stamp = getUploadStamp(file);
        const stampText = [stamp.date, stamp.time].filter(Boolean).join(' · ');

        if (viewMode === 'list') {
            return (
                <div
                    key={file.uri}
                    className="flex items-center gap-3 rounded-xl border border-[#E5E7EB] px-3 py-2 hover:border-[#7C5CFC] hover:bg-[#FBFAFF] transition-colors"
                >
                    <div className="h-10 w-10 flex-shrink-0 overflow-hidden rounded-lg">
                        <BankStatementThumb uri={file.uri} loadPdf={loadPdf} heightClass="h-10" />
                    </div>
                    <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-semibold text-[#1F2937]">{file.file_name}</p>
                        {stampText ? <p className="truncate text-xs text-[#6B7280]">{stampText}</p> : null}
                    </div>
                    <button
                        type="button"
                        onClick={() => setPreviewIndex(idx)}
                        title="Preview"
                        className="rounded-lg p-2 text-[#6B7280] hover:bg-[#EFEAFE] hover:text-[#5B3FE0] transition-colors"
                    >
                        <EyeIcon className="h-4 w-4" />
                    </button>
                    <button
                        type="button"
                        onClick={() => downloadFile(file)}
                        title="Download"
                        className="rounded-lg p-2 text-[#6B7280] hover:bg-[#EFEAFE] hover:text-[#5B3FE0] transition-colors"
                    >
                        <DownloadIcon className="h-4 w-4" />
                    </button>
                </div>
            );
        }

        return (
            <div
                key={file.uri}
                role="button"
                tabIndex={0}
                onClick={() => setPreviewIndex(idx)}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setPreviewIndex(idx);
                    }
                }}
                className="group flex cursor-pointer flex-col overflow-hidden rounded-xl border border-[#E5E7EB] text-left hover:border-[#7C5CFC] hover:shadow-sm transition-all"
                title={`Preview ${file.file_name}`}
            >
                <div className="relative">
                    <BankStatementThumb uri={file.uri} loadPdf={loadPdf} heightClass="h-24" />
                    <div className="absolute right-1.5 top-1.5 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                            type="button"
                            onClick={(e) => {
                                e.stopPropagation();
                                setPreviewIndex(idx);
                            }}
                            title="Preview"
                            className="rounded-md bg-white/90 p-1 text-[#5B3FE0] shadow-sm hover:bg-white"
                        >
                            <EyeIcon className="h-3.5 w-3.5" />
                        </button>
                        <button
                            type="button"
                            onClick={(e) => {
                                e.stopPropagation();
                                downloadFile(file);
                            }}
                            title="Download"
                            className="rounded-md bg-white/90 p-1 text-[#5B3FE0] shadow-sm hover:bg-white"
                        >
                            <DownloadIcon className="h-3.5 w-3.5" />
                        </button>
                    </div>
                </div>
                <div className="px-2 py-1.5">
                    <p className="truncate text-[11px] font-semibold text-[#1F2937]">{file.file_name}</p>
                    {stampText ? <p className="truncate text-[10px] text-[#9CA3AF]">{stampText}</p> : null}
                </div>
            </div>
        );
    };

    const renderGroup = (group) => {
        const isOpen = !collapsed[group.key];
        return (
            <div key={group.key} className="rounded-xl border border-[#E5E7EB] overflow-hidden">
                <button
                    type="button"
                    onClick={() => toggle(group.key)}
                    className="flex w-full items-center justify-between gap-3 bg-[#F5F6FA] px-4 py-3 text-left text-sm font-semibold text-[#1F2937] hover:bg-[#EFEAFE] transition-colors"
                >
                    <span className="flex items-center gap-2">
                        {group.name}
                        <span className="font-bold text-[#16A34A]">({group.files.length})</span>
                    </span>
                    <ChevronDownIcon
                        className={`h-4 w-4 text-[#6B7280] transition-transform ${isOpen ? 'rotate-180' : ''}`}
                    />
                </button>
                {isOpen ? (
                    <div className="px-4 py-4">
                        {group.files.length === 0 ? (
                            <p className="text-sm text-[#9CA3AF]">No PDF statements in this upload.</p>
                        ) : viewMode === 'grid' ? (
                            <div
                                style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
                                    gap: '12px',
                                }}
                            >
                                {group.files.map(renderFile)}
                            </div>
                        ) : (
                            <div className="space-y-2">{group.files.map(renderFile)}</div>
                        )}
                    </div>
                ) : null}
            </div>
        );
    };

    return (
        <div className="py-4 border-b border-[#F0F1F5] last:border-b-0">
            <h4 className="text-base font-bold text-[#1F2937] mb-3">
                {applicant.name}{' '}
                <span className="text-sm font-normal text-[#6B7280]">Bank Statements</span>
            </h4>

            {statements == undefined ? (
                <p className="text-sm text-[#6B7280]">This applicant is yet to upload any bank statements</p>
            ) : (
                <>
                    <div className="mb-4 flex flex-wrap items-center gap-2">
                        <SearchInput
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder="Search statements..."
                            className="min-w-[180px] flex-1"
                        />
                        <div className="inline-flex overflow-hidden rounded-xl border border-[#E5E7EB]">
                            <button
                                type="button"
                                onClick={() => setViewMode('grid')}
                                aria-label="Grid view"
                                className={`p-2.5 transition-colors ${
                                    viewMode === 'grid'
                                        ? 'bg-[#EFEAFE] text-[#5B3FE0]'
                                        : 'bg-white text-[#6B7280] hover:bg-[#F5F6FA]'
                                }`}
                            >
                                <GridIcon className="h-4 w-4" />
                            </button>
                            <button
                                type="button"
                                onClick={() => setViewMode('list')}
                                aria-label="List view"
                                className={`border-l border-[#E5E7EB] p-2.5 transition-colors ${
                                    viewMode === 'list'
                                        ? 'bg-[#EFEAFE] text-[#5B3FE0]'
                                        : 'bg-white text-[#6B7280] hover:bg-[#F5F6FA]'
                                }`}
                            >
                                <ListIcon className="h-4 w-4" />
                            </button>
                        </div>
                        <select
                            value={sort}
                            onChange={(e) => setSort(e.target.value)}
                            aria-label="Sort statements"
                            className="rounded-xl border border-[#E5E7EB] bg-white px-3 py-2.5 text-sm font-semibold text-[#6B7280] focus:outline-none focus:ring-2 focus:ring-[#7C5CFC]"
                        >
                            <option value="recent">Most recent</option>
                            <option value="oldest">Oldest first</option>
                            <option value="name">Name (A-Z)</option>
                        </select>
                    </div>

                    {groups.length === 0 ? (
                        <p className="text-sm text-[#9CA3AF]">
                            {query ? 'No statements match your search.' : 'No statements uploaded yet.'}
                        </p>
                    ) : (
                        <div className="space-y-2">{groups.map(renderGroup)}</div>
                    )}
                </>
            )}

            <FilePreviewModal
                files={flatFiles}
                index={previewIndex}
                onIndex={setPreviewIndex}
                onClose={() => setPreviewIndex(null)}
                loadPreview={loadPreview}
                downloadFile={downloadFile}
            />
        </div>
    );
};

export default BankStatementsApplicant;
